import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Remember locally which cards the
 * organizer copied a link for, per game, so a reload still shows who was
 * invited that way.
 */
const KEY_PREFIX = 'santa_link_invited:';

function storageKey(boxKey: string): string {
  return `${KEY_PREFIX}${boxKey}`;
}

function parseIds(raw: string | null): number[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((id): id is number => typeof id === 'number' && Number.isInteger(id));
  } catch {
    return [];
  }
}

export function readLinkInvitedIds(boxKey: string): number[] {
  if (typeof window === 'undefined' || !boxKey) {
    return [];
  }
  try {
    return parseIds(window.localStorage.getItem(storageKey(boxKey)));
  } catch {
    return [];
  }
}

export function writeLinkInvitedIds(boxKey: string, ids: number[]): void {
  if (typeof window === 'undefined' || !boxKey) {
    return;
  }
  try {
    if (ids.length === 0) {
      window.localStorage.removeItem(storageKey(boxKey));
    } else {
      window.localStorage.setItem(storageKey(boxKey), JSON.stringify(ids));
    }
  } catch {
    /* quota / private mode */
  }
}

export function useLinkInvitedIds(boxKey: string, cardIds: number[]) {
  const [stored, setStored] = useState(() => ({ key: boxKey, ids: readLinkInvitedIds(boxKey) }));
  if (stored.key !== boxKey) setStored({ key: boxKey, ids: readLinkInvitedIds(boxKey) });
  const copiedIds = useMemo(() => {
    const valid = new Set(cardIds);
    return stored.key === boxKey ? stored.ids.filter((id) => valid.has(id)) : [];
  }, [stored, boxKey, cardIds]);
  useEffect(() => {
    writeLinkInvitedIds(boxKey, copiedIds);
  }, [boxKey, copiedIds]);

  const markLinkInvited = useCallback(
    (cardId: number) => {
      setStored((current) => {
        const ids = current.key === boxKey ? current.ids : readLinkInvitedIds(boxKey);
        return ids.includes(cardId) ? current : { key: boxKey, ids: [...ids, cardId] };
      });
    },
    [boxKey],
  );

  const copiedSet = useMemo(() => new Set(copiedIds), [copiedIds]);

  return { copiedSet, markLinkInvited };
}
